import { Button, Label, toast } from '@medusajs/ui';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { getApiErrorMessage } from '@lib/util/get-api-error-message';
import { usePasswordForm } from '@lib/hooks/use-password-form';
import ErrorMessage from '@modules/common/components/error-message';
import Input from '@modules/common/components/input';
import { useUpdatePassword } from '@lib/query/mutations/auth';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { countryCode } = useParams<{ countryCode: string }>();
  const token = searchParams.get('token');

  const [message, setMessage] = useState(
    token ? '' : '無效的重設密碼連結。請重新申請密碼重設，或聯繫客服協助。'
  );
  const {
    password1,
    password2,
    handlePassword1Change,
    handlePassword2Change,
    hasValidationError,
  } = usePasswordForm();

  const updatePassword = useUpdatePassword({
    onSuccess: () => {
      toast.success('您的密碼已成功更新', { duration: 4000 });
      navigate(`/${countryCode ?? 'tw'}/account?reset=true`);
    },
    onError: (error) => {
      setMessage(getApiErrorMessage(error, '密碼重設失敗，請稍後再試'));
    },
  });

  const confirmNewPassword = () => {
    // Guard: prevent submission if validation fails or token is missing
    if (password1.error || password2.error) {
      return;
    }
    if (!token) {
      return;
    }
    updatePassword.mutate({ token, password: password1.value });
  };

  return (
    <div
      className="mx-auto flex max-w-[644px] flex-col items-center gap-y-12"
      data-testid="login-page"
    >
      <div className="text-black-40 self-start text-xl font-bold">
        重新設定密碼
      </div>
      <div className="flex w-full flex-col">
        <Label className="text-black-10 text-base">新密碼*</Label>
        <Input
          name="password1"
          type="password"
          autoComplete="new-password"
          placeholder="請設定 8 位以上的英文加數字的密碼"
          value={password1.value}
          onChange={handlePassword1Change}
          required
          data-testid="email-input"
        />
        {password1.error && (
          <span className="text-red-60 text-sm">{password1.error}</span>
        )}
        <Label className="text-black-10 mt-6 text-base">
          再輸入一次新密碼*
        </Label>
        <Input
          name="password2"
          type="password"
          autoComplete="new-password"
          placeholder="請設定 8 位以上的英文加數字的密碼"
          value={password2.value}
          onChange={handlePassword2Change}
          required
          data-testid="password-input"
        />
        {password2.error && (
          <span className="text-red-60 text-sm">{password2.error}</span>
        )}
      </div>
      <ErrorMessage error={message} data-testid="login-error-message" />
      <div className="flex justify-center">
        <Button
          data-testid="check-button"
          className="text-white-10 disabled:bg-white-40 disabled:text-white-10 rounded-sm bg-red-50 px-6 py-3 text-base font-bold shadow-none disabled:shadow-none"
          onClick={confirmNewPassword}
          disabled={
            !token ||
            hasValidationError ||
            updatePassword.isPending
          }
          isLoading={updatePassword.isPending}
        >
          確認
        </Button>
      </div>
    </div>
  );
};

export default ResetPassword;
